'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { trackAiReferralOnce } from '@/lib/analytics';

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-4GLRWQYS39';

/**
 * Get cookie consent preferences from localStorage
 */
function getCookieConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const stored = localStorage.getItem('cookie-consent-preferences');
    if (!stored) return false;
    
    const parsed = JSON.parse(stored);
    return parsed.preferences?.analytics === true;
  } catch {
    return false;
  }
}

export function GoogleAnalytics() {
  const [hasConsent, setHasConsent] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    
    // Don't load analytics on privacy/legal pages
    const path = window.location.pathname;
    const excludedPaths = ['/privacy', '/vision', '/contact-us', '/trademark-policy', '/careers', '/region'];
    const isExcludedPage = excludedPaths.some(excluded => path.includes(excluded));
    
    if (isExcludedPage) {
      return;
    }
    
    // Check initial consent
    setHasConsent(getCookieConsent());

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent) => {
      const newConsent = event.detail?.analytics === true;
      setHasConsent(newConsent);
      
      // If consent is granted, initialize GA
      if (newConsent && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
      
      // If consent is revoked, update GA consent
      if (!newConsent && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    };

    window.addEventListener('cookiePreferencesChanged', handleConsentChange as EventListener);
    
    return () => {
      window.removeEventListener('cookiePreferencesChanged', handleConsentChange as EventListener);
    };
  }, []);

  // Only load GA scripts if user has given consent
  if (!isClient || !hasConsent) {
    return null;
  }

  return (
    <>
      {/* Use lazyOnload strategy to defer loading until after page is interactive */}
      <Script strategy="lazyOnload" src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
      <Script
        id="google-analytics"
        strategy="lazyOnload"
        onLoad={() => {
          // Detect AI source and fire ai_referral event + set ai_source user
          // property. Runs once gtag is initialized; idempotent across page
          // navigations within the same session.
          trackAiReferralOnce();
        }}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA_MEASUREMENT_ID}', {
              page_path: window.location.pathname,
              // Cookie settings for privacy and third-party cookie compatibility
              cookie_flags: 'SameSite=None;Secure',
              // Anonymize IP addresses
              anonymize_ip: true,
              // Disable ads features to reduce third-party cookies
              allow_google_signals: false,
              allow_ad_personalization_signals: false,
            });
          `,
        }}
      />
    </>
  );
}

